import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import { runPool } from '../common/utils';
import { motornetVehicleQueryOptions } from '../common/useMotornetVehicle';
import {
	colorOptionalIndex,
	type ColorOptionalIndex,
} from '../common/motornetToForm';
import type { MotornetVehicleKind } from '../types';

export interface VehicleCatalogKey {
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
}

export const catalogKeyOf = (key: VehicleCatalogKey): string =>
	`${key.vehicleKind}:${key.eurotaxCode}`;

export interface FailedCatalog {
	key: string;
	error: string;
}

export interface CatalogState {
	index: ColorOptionalIndex | null;
	error: string | null;
	/** non ancora risolto: include anche il "non ancora partito" */
	pending: boolean;
}

const CONCURRENCY = 6;

const errorMessage = (reason: unknown): string =>
	(reason as { message?: string })?.message || 'Caricamento listino fallito';

export const useImportOptionalCatalogs = (keys: VehicleCatalogKey[]) => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const apiClientRef = useRef(apiClient);
	apiClientRef.current = apiClient;

	const keysRef = useRef(keys);
	keysRef.current = keys;

	const [indexes, setIndexes] = useState<Record<string, ColorOptionalIndex>>(
		{}
	);
	const [failed, setFailed] = useState<Record<string, string>>({});
	const [progress, setProgress] = useState({ total: 0, done: 0, error: 0 });
	const [isInitialLoading, setIsInitialLoading] = useState(false);

	const inFlightRef = useRef<Set<string>>(new Set());
	const initialDoneRef = useRef(false);

	const load = useCallback(
		async (targets: VehicleCatalogKey[]) => {
			if (!targets.length) return;
			targets.forEach((key) => inFlightRef.current.add(catalogKeyOf(key)));
			setProgress({ total: targets.length, done: 0, error: 0 });

			const loaded: Record<string, ColorOptionalIndex> = {};
			const errors: Record<string, string> = {};

			await runPool(
				targets,
				async (key) => {
					const vehicle = await queryClient.fetchQuery(
						motornetVehicleQueryOptions(
							apiClientRef.current,
							key.eurotaxCode,
							key.vehicleKind,
							null,
							null,
							{ retry: 1 }
						)
					);
					return colorOptionalIndex(vehicle);
				},
				{
					concurrency: CONCURRENCY,
					onSettle: (key, result) => {
						const id = catalogKeyOf(key);
						inFlightRef.current.delete(id);
						if (result.status === 'fulfilled') loaded[id] = result.value;
						else errors[id] = errorMessage(result.reason);
						setProgress((prev) => ({
							...prev,
							done: prev.done + 1,
							error: result.status === 'rejected' ? prev.error + 1 : prev.error,
						}));
					},
				}
			);

			setIndexes((prev) => ({ ...prev, ...loaded }));
			setFailed((prev) => ({ ...prev, ...errors }));
		},
		[queryClient]
	);

	const signature = keys.map(catalogKeyOf).join('|');

	useEffect(() => {
		const missing = keysRef.current.filter((key) => {
			const id = catalogKeyOf(key);
			return !indexes[id] && !failed[id] && !inFlightRef.current.has(id);
		});
		if (!missing.length) return;

		const isFirstRun = !initialDoneRef.current;
		if (isFirstRun) setIsInitialLoading(true);
		void load(missing).finally(() => {
			if (isFirstRun) {
				initialDoneRef.current = true;
				setIsInitialLoading(false);
			}
		});
	}, [signature, indexes, failed, load]);

	const stateFor = useCallback(
		(key: VehicleCatalogKey | null | undefined): CatalogState => {
			if (!key) return { index: null, error: null, pending: false };
			const id = catalogKeyOf(key);
			if (indexes[id])
				return { index: indexes[id], error: null, pending: false };
			if (failed[id]) return { index: null, error: failed[id], pending: false };
			return { index: null, error: null, pending: true };
		},
		[indexes, failed]
	);

	const failedCatalogs = useMemo<FailedCatalog[]>(
		() => Object.entries(failed).map(([key, error]) => ({ key, error })),
		[failed]
	);

	const retryFailed = useCallback(async () => {
		const ids = new Set(Object.keys(failed));
		if (ids.size === 0) return;
		const targets = keysRef.current.filter((key) => ids.has(catalogKeyOf(key)));
		setFailed({});
		await load(targets);
	}, [failed, load]);

	const reset = useCallback(() => {
		setFailed({});
		setProgress({ total: 0, done: 0, error: 0 });
		initialDoneRef.current = false;
	}, []);

	return {
		stateFor,
		progress,
		isInitialLoading,
		failedCatalogs,
		retryFailed,
		reset,
	};
};
